import { activateSiblingButton, byId, matchCall, textareaValue } from "../domUtils";
import type { PrototypeState } from "../controllerState";
import type { Root } from "../prototypeTypes";
import { currentPaywallRequestGeneration, matchBillingCycle, openPaywallWithLayout, previewUpgrade, retryPaywallSubscription, toPlanCode } from "../commerceController";
import { readSubscriptionPurchaseIntent, selectSubscriptionChooserPlan, setSubscriptionChooserBillingCycle, shouldOpenPaywallForPublicUpgrade } from '../subscriptionChooserController';
import type { SubscriptionPurchaseIntent } from '../subscriptionChooserController';
import { isSubscriptionUpgradeSelectionAllowed } from '../subscriptionChooserState';
import { refreshHomeState } from "../homeStateController";
import { activateTier } from "../playerUiController";
import { failValidation, recordRuntimeError } from "../prototypeRuntimeSupport";
import { checkClientIntent, handleApiError } from "../authGuards";
import { openModal } from "../navigationController";
import { loadIdeaComments } from "../ideaCommentsController";
import { currentIdeaDetailGeneration, isCurrentIdeaDetail } from '../ideaDetailController';
import { applyCommunityListFilter, runCommunitySearch, clearCommunitySearch } from "../viewHydrator";
import { applyGithubTrendingListFilter, runGhSearch, clearGhSearch } from "../githubTrendingHydrator";
import { showToast } from "../toastController";
import { PrototypeApiError } from "../apiTransport";
import { recordPendingCheckoutIntent } from '../pendingCheckoutIntent';
import { renderRegisterTurnstile } from '../turnstileWidgetController';
import { getRecoverableTurnstileToken, isTurnstileClientError } from '../turnstileRuntime';
import { isVotable, openVotePaywall, submitCommunityVote } from '../communityVoteController';
import { parseBillingUpgradePreviewResponse, type BillingUpgradePreviewResponse } from '../apiClientResponses';
import { clearFieldError, focusFirstFieldError, showFieldError } from '../wizardValidation';
import { clearOperationFeedback, paintOperationFeedback } from '../operationFeedback';
import { showAuthModalIntentNote } from '../formProfileController';

const signupTurnstileCallbacks = { onSuccess: () => {}, onError: () => {}, onExpired: () => {} };
const commentAttempts = new WeakMap<object, symbol>();
const upgradePreparationGenerations = new WeakMap<Root, number>();

export function invalidateUpgradePreparation(root: Root): number {
	const generation = (upgradePreparationGenerations.get(root) ?? 0) + 1;
	upgradePreparationGenerations.set(root, generation);
	return generation;
}

export async function routeBillingAndCommunityActions(normalized: string, root: Root, source: HTMLElement, state: PrototypeState) {
	if (normalized.includes('retryPaywallSubscription')) {
		retryPaywallSubscription(root, state);
		return;
	}
  const billingCycle = matchBillingCycle(normalized);
	if (billingCycle) {
	if (source.hasAttribute('disabled') || source.getAttribute('aria-disabled') === 'true') return;
	invalidateUpgradePreparation(root);
	setSubscriptionChooserBillingCycle(root, source, billingCycle);
		return;
  }
	if (normalized.includes('selectSubscriptionPlan')) {
		invalidateUpgradePreparation(root);
		selectSubscriptionChooserPlan(root, source);
		return;
	}
	if (normalized.includes('chooseSubscriptionPlan')) {
		const preparationGeneration = invalidateUpgradePreparation(root);
		const nextPlan = toPlanCode(matchCall(normalized, 'chooseSubscriptionPlan'));
		if (!nextPlan || nextPlan === 'visitor' || nextPlan === 'free') return;
		const intent = readSubscriptionPurchaseIntent(root, source, nextPlan);
		if (!intent) {
			if (shouldOpenPaywallForPublicUpgrade(root, source, nextPlan)) openPaywallWithLayout(root, state);
			return;
		}
		const actionFeedbackId = `${intent.instanceId}ActionFeedback`;
		const paywallGeneration = intent.source === 'paywall' ? currentPaywallRequestGeneration(root) : undefined;
		clearOperationFeedback(root, actionFeedbackId);
		if (
			intent.billingPlan !== 'free'
			&& (
				!intent.currentBillingCycle
				|| !intent.providerSubscriptionId
				|| !isSubscriptionUpgradeSelectionAllowed({
					fromPlan: intent.billingPlan,
					fromCycle: intent.currentBillingCycle,
					toPlan: intent.targetPlan,
					toCycle: intent.billingCycle,
					crossCycleUpgrade: state.runtime.crossCycleUpgradeEnabled === true,
				})
			)
		) return;
		if (intent.billingPlan === 'free' && state.currentPlan === 'visitor') {
			recordPendingCheckoutIntent(intent.targetPlan, intent.billingCycle, intent.source);
			openModal(root, 'registerModal');
			showAuthModalIntentNote(root, 'register', `Create an account to continue with ${intent.targetPlan.charAt(0).toUpperCase() + intent.targetPlan.slice(1)}. You will confirm payment before any charge.`);
			void renderRegisterTurnstile(root, state.runtime, signupTurnstileCallbacks);
			return;
		}
		if (intent.billingPlan !== 'free') {
				try {
					const preview = parseBillingUpgradePreviewResponse(await state.runtime.api.previewUpgrade({
						toPlan: intent.targetPlan,
						billingCycle: intent.billingCycle,
					}));
					if (!ownsUpgradePreparation(root, source, intent, preparationGeneration, paywallGeneration)) return;
					if (!upgradePreviewMatchesIntent(preview, intent)) throw new Error('billing_upgrade_preview_mismatch');
					// 付费升级先出确认框（文案按服务器预览的 behavior 区分：同周期只收差额 /
					// 跨周期退款并开新账期），确认后才真正扣款。跨周期升级无中间付款页（Creem API 直接扣）。
					const { presentUpgradeConfirmPrompt } = await import('../actions/billingUpgradeConfirmPrompt');
					if (!ownsUpgradePreparation(root, source, intent, preparationGeneration, paywallGeneration)) return;
					presentUpgradeConfirmPrompt(root, preview, (canWriteUi) =>
						previewUpgrade(intent.targetPlan, root, state, recordRuntimeError, {
							providerSubscriptionId: preview.subscriptionId, source: intent.source, billingCycle: intent.billingCycle, canWriteUi,
						}));
				} catch (error) {
					if (!ownsUpgradePreparation(root, source, intent, preparationGeneration, paywallGeneration)) return;
					recordRuntimeError(state.runtime, error);
					paintOperationFeedback(root, actionFeedbackId, 'We could not prepare your upgrade. Please try again.');
				}
			return;
		}
		const outcome = await previewUpgrade(intent.targetPlan, root, state, recordRuntimeError, {
			source: intent.source, billingCycle: intent.billingCycle,
		});
		if (!outcome.ok && 'message' in outcome) paintOperationFeedback(root, actionFeedbackId, outcome.message);
		return;
	}
	if (normalized.includes('selectTier')) activateTier(source);
  if (normalized.includes("voteIdea")) {
    if (openVotePaywall(root, state)) return;
    if (!isVotable(source)) return;
    await submitCommunityVote(root, state, source, 1);
  }
  if (normalized.includes("voteDown")) {
    if (openVotePaywall(root, state)) return;
    if (!isVotable(source)) return;
    await submitCommunityVote(root, state, source, -1);
  }
  if (normalized.includes("filterCommunity")) {
    const filter = matchCall(normalized, "filterCommunity") ?? "recent";
    filterCommunity(root, source, filter);
  }
  if (normalized.includes("runCommunitySearch")) {
    runCommunitySearch(root, source);
  }
  if (normalized.includes("clearCommunitySearch")) {
    clearCommunitySearch(root, source);
  }
  if (normalized.includes("addIdeaComment")) {
    await addIdeaComment(root, state);
  }
  if (normalized.includes("selectIdeaCat") || normalized.includes("selectFeedbackCat") || normalized.includes("selectAiCat")) activateSiblingButton(source, source.classList[0]);
  if (normalized.includes("filterGhRepos") || normalized.includes("filterGhTrending")) {
    activateSiblingButton(source, "category-pill");
    const period = matchCall(normalized, "filterGhTrending") ?? "completed";
    applyGithubTrendingListFilter(root, period, source);
  }
  if (normalized.includes("runGhSearch")) {
    runGhSearch(root, source);
  }
  if (normalized.includes("clearGhSearch")) {
    clearGhSearch(root, source);
  }
}

function ownsUpgradePreparation(root: Root, source: HTMLElement, intent: SubscriptionPurchaseIntent, generation: number, paywallGeneration?: number): boolean {
	if (upgradePreparationGenerations.get(root) !== generation) return false;
	if (intent.source === 'paywall' && currentPaywallRequestGeneration(root) !== paywallGeneration) return false;
	const current = readSubscriptionPurchaseIntent(root, source, intent.targetPlan);
	if (!current || !samePurchaseIntent(current, intent)) return false;
	const panel = root.querySelector<HTMLElement>(`[data-prefix="${intent.instanceId}"]`);
	if (!panel || !panel.isConnected) return false;
	const modal = panel.closest<HTMLElement>('.modal-overlay');
	if (modal) return modal.classList.contains('active') && (intent.source !== 'paywall' || modal.id === 'paywallModal');
	const view = panel.closest<HTMLElement>('.view');
	return !view || view.classList.contains('active');
}

function samePurchaseIntent(left: SubscriptionPurchaseIntent, right: SubscriptionPurchaseIntent): boolean {
	return left.instanceId === right.instanceId
		&& left.source === right.source
		&& left.billingPlan === right.billingPlan
		&& left.targetPlan === right.targetPlan
		&& left.billingCycle === right.billingCycle
		&& left.currentBillingCycle === right.currentBillingCycle
		&& left.providerSubscriptionId === right.providerSubscriptionId;
}

export function upgradePreviewMatchesIntent(
	preview: BillingUpgradePreviewResponse,
	intent: Pick<SubscriptionPurchaseIntent, 'billingPlan' | 'targetPlan' | 'currentBillingCycle' | 'billingCycle' | 'providerSubscriptionId'>,
): boolean {
	if (
		intent.billingPlan === 'free'
		|| !intent.currentBillingCycle
		|| !intent.providerSubscriptionId
		|| preview.subscriptionId !== intent.providerSubscriptionId
		|| preview.fromPlan !== intent.billingPlan
		|| preview.toPlan !== intent.targetPlan
		|| preview.billingCycle !== intent.billingCycle
	) return false;
	const expectedBehavior = intent.currentBillingCycle === intent.billingCycle
		? 'same_cycle_proration'
		: 'cross_cycle_refund_and_rebill';
	if (preview.behavior !== expectedBehavior) return false;
	return preview.behavior !== 'cross_cycle_refund_and_rebill' || preview.fromBillingCycle === intent.currentBillingCycle;
}

function filterCommunity(root: Root, source: HTMLElement, filter: string) {
  source.closest('[data-community-role="filters"]')?.querySelectorAll(".category-pill").forEach((node) => node.classList.remove("active"));
  source.classList.add("active");
  applyCommunityListFilter(root, filter, source);
}

async function addIdeaComment(root: Root, state: PrototypeState) {
  const decision = checkClientIntent(state, "commentIdea");
  if (!decision.allowed) {
    if (decision.reason === "login") {
      openModal(root, "loginModal");
      return;
    }
    openPaywallWithLayout(root, state, {
      setIntent(targetRoot) {
        const target = targetRoot.querySelector<HTMLElement>("#paywallTargetPlan");
        if (target) target.textContent = decision.requiredPlan;
      },
    });
    return;
  }
	const ideaId = state.runtime.__activeIdeaSlug;
  if (!ideaId) {
    failValidation("Open an idea before commenting.");
    return;
  }
  const body = textareaValue(root, "ideaCommentInput").trim();
	if (body.length < 2 || body.length > 1000) {
		showFieldError(root, 'ideaCommentInput', body.length > 1000 ? 'Comments must not exceed 1000 characters.' : 'Comments must be at least 2 characters.');
    focusFirstFieldError(root, ['ideaCommentInput']);
    return;
  }
	clearFieldError(root, 'ideaCommentInput');
	const generation = currentIdeaDetailGeneration(root);
	const attempt = Symbol('idea-comment');
	commentAttempts.set(root as object, attempt);
	const isCurrent = () => commentAttempts.get(root as object) === attempt && isCurrentIdeaDetail(root, ideaId, generation);
  // Post Comment 按钮：置灰 + loading 态（[data-loading] 约定），防重复提交
  const commentBtn = root.querySelector<HTMLButtonElement>('[data-proto-click="addIdeaComment()"]');
  const originalBtnText = commentBtn?.textContent ?? 'Post Comment';
  const setBtnBusy = (busy: boolean) => {
    if (!commentBtn) return;
    commentBtn.disabled = busy;
    commentBtn.setAttribute('aria-busy', String(busy));
    if (busy) { commentBtn.dataset.loading = ''; commentBtn.textContent = 'Posting...'; }
    else { delete commentBtn.dataset.loading; commentBtn.textContent = originalBtnText; }
  };
	setBtnBusy(true);
	try {
		const turnstileToken = await getRecoverableTurnstileToken(state.runtime, "community-comment-submit");
		if (!isCurrent()) return;
		await state.runtime.api.commentCommunityIdea(ideaId, { body, turnstileToken });
		if (!isCurrent()) return;
		const input = byId(root, "ideaCommentInput") as HTMLTextAreaElement | null;
		if (input) input.value = "";
		await loadIdeaComments(root, ideaId, isCurrent);
	} catch (error) {
		if (!isCurrent()) return;
    if (error instanceof PrototypeApiError && error.status === 429) {
      showToast({ type: 'error', message: 'Commenting too fast. Please wait a moment.' });
      return;
    }
    if (isTurnstileClientError(error)) {
      showToast({ type: 'info', message: 'Verification failed. Please try again.', durationMs: 3000 });
      return;
    }
		handleApiError(state.runtime, state.effectiveAccessPlan, state.isLoggedIn, error, { root, intent: 'comment' });
	} finally {
		if (isCurrent()) setBtnBusy(false);
	}
}
